// Demo data store. In-memory only — sandbox blocks localStorage.
// Two seed accounts; one matches the composite-math worksheet (NorthMap).
//
// DEPRECATED: loadAccounts(), getAccount(), updateAccountAnswers(), and saveAccounts()
// are no longer called by AccountList, AccountScorecard, DimensionFlow, NewAccount,
// or Manager. Those pages now use the live API via api() from @/lib/session.
// The AccountSeed type and seed-data functions are kept for potential use by other
// modules (e.g., plan-report pages). Do not delete this file.

import type { AnswerMap } from "./scoring";

// In-memory shim that mimics the Storage interface so the rest of this
// module reads the same way it did when backed by localStorage.
const memoryStore: Map<string, string> = new Map();
const memStorage = {
  getItem: (k: string): string | null => (memoryStore.has(k) ? memoryStore.get(k)! : null),
  setItem: (k: string, v: string): void => { memoryStore.set(k, v); },
  removeItem: (k: string): void => { memoryStore.delete(k); },
};

export interface AccountSeed {
  id: string;
  company: string;
  industry: string;
  arr: number;
  stage: string;
  rep: string;
  champion: string;
  note: string;
  answers: AnswerMap;
}

const STORAGE_KEY = "mi6.aip.accounts.v1";

/* ----- Seed answers for NorthMap (Will Winsome) — drives composite ~44.1 -- */
const NORTHMAP_ANSWERS: AnswerMap = {
  // Tech Approval — TBD-dominant (64%)
  "Q1.TA-01": "B",
  "Q1.TA-02": "E",
  "Q1.TA-04": "F", // TBD (option F is TBD on Q1.TA-04 — 6 options A..F)
  "Q1.TA-05": "D",
  "Q1.TA-07": "TBD_LOOKUP", // resolved below
  "Q1.TA-14": "D",
  // remaining 8 marked TBD via lookup below

  // Finance — Red, 44% TBD
  "Q2.FP-01": "B",
  "Q2.FP-06": "A",
  "Q2.FP-07": "B",
  "Q2.FP-09": "C",
  "Q2.FP-10": "A",
  "Q2.FP-13": "D",

  // IT/Sec — TBD-dominant (55%)
  "Q3.IS-01": "A",
  "Q3.IS-02": "C",
  "Q3.IS-07": "B",
  "Q3.IS-08": "A",
  "Q3.IS-11": "C",

  // Champion — Amber (60.3)
  "Q4.CS-01": "A",
  "Q4.CS-02": "C",
  "Q4.CS-03": "B",
  "Q4.CS-04": "D",
  "Q4.CS-05": "C",
  "Q4.CS-07": "A",
  "Q4.CS-08": "A",
  "Q4.CS-09": "C",
  "Q4.CS-10": "D",
  "Q4.CS-11": "D",
  "Q4.CS-12": "A",
  "Q4.CS-13": "C",

  // Pain — Amber (74.7)
  "Q5.PU-01": "A",
  "Q5.PU-02": "B",
  "Q5.PU-03": "A",
  "Q5.PU-04": "B",
  "Q5.PU-05": "B",
  "Q5.PU-06": "C",
  "Q5.PU-07": "B",
  "Q5.PU-08": "C",
  "Q5.PU-09": "B",
  "Q5.PU-10": "A",

  // Timing — Amber (57.8)
  "Q6.TC-01": "A",
  "Q6.TC-02": "B",
  "Q6.TC-03": "C",
  "Q6.TC-04": "A",
  "Q6.TC-05": "D",
  "Q6.TC-06": "A",
  "Q6.TC-08": "A",
  "Q6.TC-09": "A",

  // Competition — Amber (51.8)
  "Q7.CO-01": "B",
  "Q7.CO-02": "A",
  "Q7.CO-03": "B",
  "Q7.CO-04": "C",
  "Q7.CO-05": "C",
  "Q7.CO-06": "B",
  "Q7.CO-07": "D",
  "Q7.CO-08": "D",
  "Q7.CO-09": "C",

  // Legal — Red (33.75)
  "Q8.LG-01": "A",
  "Q8.LG-02": "B",
  "Q8.LG-03": "E",
  "Q8.LG-04": "A",
  "Q8.LG-06": "D",
  "Q8.LG-07": "C",
  "Q8.LG-08": "D",
};

// post-processing: convert markers (and add TBD answers) — done by index
// We mark all unanswered Tech parents as TBD ("F" or last letter) below.

function tbdLetterFor(qid: string): string {
  // We rely on the scoring engine treating "any letter that maps to a TBD option"
  // as TBD. To stay generic, the seed below uses a "*TBD*" sentinel which the
  // scoring engine doesn't know. Instead we resolve the actual TBD letter for
  // each question using the question bank.
  return "TBD";
}

// Resolve sentinel TBD answers and add explicit TBDs for remaining tech parents.
import { QBANK, findOption, findQuestion, parentQuestions, type AnswerMap as _AM } from "./scoring";

function buildNorthMapAnswers(): AnswerMap {
  const out: AnswerMap = {};
  // copy all explicit answers, mapping fake markers to actual TBD letter
  for (const [qid, val] of Object.entries(NORTHMAP_ANSWERS)) {
    if (val === "TBD_LOOKUP") {
      // find TBD option letter for that question
      for (const d of QBANK.dimensions) {
        const q = findQuestion(d, qid);
        if (q) {
          const tbd = q.answer_options.find((o) => o.is_tbd);
          if (tbd) out[qid] = tbd.letter;
          break;
        }
      }
    } else {
      out[qid] = val;
    }
  }

  // Tech: ensure remaining parent questions are TBD'd
  const techDim = QBANK.dimensions.find((d) => d.dimension_id === "tech_approval")!;
  for (const p of parentQuestions(techDim)) {
    if (!(p.question_id in out)) {
      const tbd = p.answer_options.find((o) => o.is_tbd);
      if (tbd) out[p.question_id] = tbd.letter;
    }
  }

  // Finance: remaining parents TBD
  const finDim = QBANK.dimensions.find((d) => d.dimension_id === "finance")!;
  for (const p of parentQuestions(finDim)) {
    if (!(p.question_id in out)) {
      const tbd = p.answer_options.find((o) => o.is_tbd);
      if (tbd) out[p.question_id] = tbd.letter;
    }
  }

  // IT/Sec: remaining parents TBD
  const itDim = QBANK.dimensions.find((d) => d.dimension_id === "it_security")!;
  for (const p of parentQuestions(itDim)) {
    if (!(p.question_id in out)) {
      const tbd = p.answer_options.find((o) => o.is_tbd);
      if (tbd) out[p.question_id] = tbd.letter;
    }
  }

  // Legal: Q8.LG-05 TBD per worksheet
  if (!("Q8.LG-05" in out)) {
    const legalDim = QBANK.dimensions.find((d) => d.dimension_id === "legal")!;
    const q = findQuestion(legalDim, "Q8.LG-05");
    if (q) {
      const tbd = q.answer_options.find((o) => o.is_tbd);
      if (tbd) out[q.question_id] = tbd.letter;
    }
  }

  return out;
}

function buildHopeSpringsAnswers(): AnswerMap {
  // A second deal — Hope Springs / Bryant Industries — partly worked, mid-Amber composite.
  // Designed as a contrast: strong tech, weak champion. Most parents answered.
  const out: AnswerMap = {};
  // Tech — green-ish
  out["Q1.TA-01"] = "A"; out["Q1.TA-02"] = "B"; out["Q1.TA-03"] = "B";
  out["Q1.TA-04"] = "B"; out["Q1.TA-05"] = "A"; out["Q1.TA-06"] = "A";
  out["Q1.TA-07"] = "A"; out["Q1.TA-08"] = "B"; out["Q1.TA-09"] = "A";
  out["Q1.TA-10"] = "A"; out["Q1.TA-11"] = "B"; out["Q1.TA-12"] = "A";
  out["Q1.TA-13"] = "A"; out["Q1.TA-14"] = "B";

  // Finance — amber
  out["Q2.FP-01"] = "A"; out["Q2.FP-02"] = "B"; out["Q2.FP-03"] = "A";
  out["Q2.FP-04"] = "B"; out["Q2.FP-05"] = "A"; out["Q2.FP-06"] = "A";
  out["Q2.FP-07"] = "A"; out["Q2.FP-08"] = "B"; out["Q2.FP-09"] = "B";
  out["Q2.FP-10"] = "A"; out["Q2.FP-11"] = "B"; out["Q2.FP-12"] = "B"; out["Q2.FP-13"] = "B";

  // IT — green
  out["Q3.IS-01"] = "A"; out["Q3.IS-02"] = "B"; out["Q3.IS-03"] = "A";
  out["Q3.IS-04"] = "A"; out["Q3.IS-05"] = "A"; out["Q3.IS-06"] = "A";
  out["Q3.IS-07"] = "A"; out["Q3.IS-08"] = "A"; out["Q3.IS-09"] = "B";
  out["Q3.IS-10"] = "A"; out["Q3.IS-11"] = "A";

  // Champion — RED (weak champion in this deal)
  out["Q4.CS-01"] = "C"; // weak / not advocating
  out["Q4.CS-02"] = "D"; out["Q4.CS-03"] = "D"; out["Q4.CS-04"] = "D";
  out["Q4.CS-05"] = "D"; out["Q4.CS-07"] = "C"; out["Q4.CS-08"] = "B";
  out["Q4.CS-09"] = "C"; out["Q4.CS-10"] = "D"; out["Q4.CS-11"] = "D";
  out["Q4.CS-12"] = "B"; out["Q4.CS-13"] = "C";

  // Pain — amber
  out["Q5.PU-01"] = "B"; out["Q5.PU-02"] = "B"; out["Q5.PU-03"] = "B";
  out["Q5.PU-04"] = "B"; out["Q5.PU-05"] = "B"; out["Q5.PU-06"] = "B";
  out["Q5.PU-07"] = "B"; out["Q5.PU-08"] = "B"; out["Q5.PU-09"] = "B"; out["Q5.PU-10"] = "B";

  // Timing — amber
  out["Q6.TC-01"] = "B"; out["Q6.TC-02"] = "B"; out["Q6.TC-03"] = "B";
  out["Q6.TC-04"] = "B"; out["Q6.TC-05"] = "B"; out["Q6.TC-06"] = "B";
  out["Q6.TC-08"] = "B"; out["Q6.TC-09"] = "B";

  // Competition — green
  out["Q7.CO-01"] = "A"; out["Q7.CO-02"] = "A"; out["Q7.CO-03"] = "A";
  out["Q7.CO-04"] = "A"; out["Q7.CO-05"] = "B"; out["Q7.CO-06"] = "A";
  out["Q7.CO-07"] = "A"; out["Q7.CO-08"] = "A"; out["Q7.CO-09"] = "B";

  // Legal — green
  out["Q8.LG-01"] = "A"; out["Q8.LG-02"] = "A"; out["Q8.LG-03"] = "A";
  out["Q8.LG-04"] = "A"; out["Q8.LG-05"] = "A"; out["Q8.LG-06"] = "A";
  out["Q8.LG-07"] = "A"; out["Q8.LG-08"] = "A";

  return out;
}

function buildMistyAnswers(): AnswerMap {
  // Misty Quota — early discovery; mostly TBD. Demonstrates fully empty deal.
  return {};
}

function defaultSeed(): AccountSeed[] {
  return [
    {
      id: "northmap",
      company: "NorthMap Analytics",
      industry: "Data intelligence",
      arr: 480000,
      stage: "Discovery",
      rep: "Will Winsome",
      champion: "VP of Business Intelligence",
      note: "Three weeks into discovery. Formal timeline: competitor contract expires in 90 days. Strong champion, weak tech approval mapping.",
      answers: buildNorthMapAnswers(),
    },
    {
      id: "bryant",
      company: "Bryant Industries",
      industry: "Industrial manufacturing",
      arr: 220000,
      stage: "Qualified Opp",
      rep: "Hope Springs",
      champion: "Director, Plant Operations",
      note: "Tech and IT validated cleanly. Champion strength is the open issue — Plant Director is observing, not advocating.",
      answers: buildHopeSpringsAnswers(),
    },
    {
      id: "westmark",
      company: "Westmark Metals",
      industry: "Transportation",
      arr: 350000,
      stage: "Suspect",
      rep: "Misty Quota",
      champion: "(none yet)",
      note: "First call last week. Nothing qualified yet — full TBD scorecard expected.",
      answers: buildMistyAnswers(),
    },
  ];
}

export function loadAccounts(): AccountSeed[] {
  try {
    const raw = memStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seed = defaultSeed();
      memStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
      return seed;
    }
    return JSON.parse(raw) as AccountSeed[];
  } catch {
    return defaultSeed();
  }
}

export function saveAccounts(list: AccountSeed[]) {
  memStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function getAccount(id: string): AccountSeed | undefined {
  return loadAccounts().find((a) => a.id === id);
}

export function updateAccountAnswers(id: string, answers: AnswerMap) {
  const list = loadAccounts();
  const i = list.findIndex((a) => a.id === id);
  if (i < 0) return;
  list[i] = { ...list[i], answers };
  saveAccounts(list);
}

export function resetSeed() {
  memStorage.removeItem(STORAGE_KEY);
}
